import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Truck, Package, Users, Zap, Shield, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SEO, ORGANIZATION_SCHEMA } from "@/components/SEO";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.5 } }),
};

export default function LandingPage() {
  return (
    <main data-testid="landing-page">
      <SEO
        title="Agence d'intérim logistique et supply chain en France — cariste, préparateur de commandes, manutentionnaire"
        description="FR INTERIM, agence de travail temporaire spécialisée logistique et supply chain. Missions d'intérim, CDD, CDI dans toute la France : cariste (CACES 1/3/5), préparateur de commandes, manutentionnaire, agent logistique. Inscription gratuite."
        path="/"
        jsonLd={ORGANIZATION_SCHEMA}
      />
      {/* Hero */}
      <section className="relative min-h-[85vh] flex items-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('https://static.prod-images.emergentagent.com/jobs/c50239db-e303-4ac2-9fad-ed65c9848429/images/516f7c9b3e5d674c3e5c82b18aad874fd8b1a38705f8b5b85f269e86232565b8.png')" }}
        />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <motion.div initial="hidden" animate="visible" className="max-w-2xl">
            <motion.p variants={fadeUp} custom={0} className="text-[#E5CD96] text-xs font-bold uppercase tracking-[0.2em] mb-4">
              Agence de travail temporaire
            </motion.p>
            <motion.h1 variants={fadeUp} custom={1} className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-white leading-tight mb-6">
              L'expertise logistique au service de votre{" "}
              <span className="text-[#E5CD96]">performance</span>
            </motion.h1>
            <motion.p variants={fadeUp} custom={2} className="text-lg text-gray-200 leading-relaxed mb-8 max-w-xl">
              Dans un secteur où la réactivité et la précision sont essentielles, FR INTERIM s'impose comme un partenaire de confiance
              dédié exclusivement aux métiers de la logistique.
            </motion.p>
            <motion.div variants={fadeUp} custom={3} className="flex flex-wrap gap-4">
              <Link to="/offres">
                <Button className="bg-gradient-to-r from-[#662483] to-[#0069B4] text-white font-semibold px-8 py-3 rounded-none hover:opacity-90 transition-opacity text-base" data-testid="hero-cta-offres">
                  Voir les offres <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Link to="/auth">
                <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#3F3F3F] font-semibold px-8 py-3 rounded-none transition-colors text-base bg-transparent" data-testid="hero-cta-inscription">
                  S'inscrire
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} className="text-center max-w-3xl mx-auto mb-16">
            <motion.p variants={fadeUp} custom={0} className="text-xs font-bold uppercase tracking-[0.2em] text-[#662483] mb-3">Notre mission</motion.p>
            <motion.h2 variants={fadeUp} custom={1} className="text-3xl sm:text-4xl font-extrabold tracking-tight text-[#3F3F3F] mb-6">
              Connecter les bonnes compétences aux bons besoins
            </motion.h2>
            <motion.p variants={fadeUp} custom={2} className="text-base md:text-lg text-gray-600 leading-relaxed">
              Grâce à une connaissance approfondie des réalités du terrain, nous accompagnons les entreprises dans la gestion
              de leurs ressources humaines, tout en offrant aux candidats des opportunités adaptées à leur savoir-faire.
            </motion.p>
          </motion.div>

          {/* Specialization */}
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} className="bg-[#F0E9F3] p-8 md:p-12 mb-16">
            <motion.h3 variants={fadeUp} custom={0} className="text-xl sm:text-2xl font-bold text-[#662483] mb-4">
              Une spécialisation forte
            </motion.h3>
            <motion.p variants={fadeUp} custom={1} className="text-base text-gray-700 leading-relaxed">
              FR INTERIM a fait le choix de se concentrer sur les métiers de la logistique : préparation de commandes, manutention,
              conduite d'engins, gestion de stocks, encadrement... Cette spécialisation nous permet de comprendre précisément vos enjeux
              et d'apporter des solutions rapides, fiables et efficaces.
            </motion.p>
          </motion.div>

          {/* For Clients / For Candidates */}
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} className="border-2 border-[#0069B4] p-8 card-hover">
              <motion.div variants={fadeUp} custom={0}>
                <div className="w-12 h-12 bg-[#E6F0F7] flex items-center justify-center mb-4">
                  <Users className="w-6 h-6 text-[#0069B4]" />
                </div>
                <h3 className="text-xl font-bold text-[#3F3F3F] mb-3">Pour nos clients</h3>
                <p className="text-base text-gray-600 leading-relaxed">
                  Nous vous aidons à faire face aux pics d'activité, aux remplacements urgents
                  en vous proposant des profils qualifiés.
                </p>
              </motion.div>
            </motion.div>
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} className="border-2 border-[#662483] p-8 card-hover">
              <motion.div variants={fadeUp} custom={0}>
                <div className="w-12 h-12 bg-[#F0E9F3] flex items-center justify-center mb-4">
                  <Package className="w-6 h-6 text-[#662483]" />
                </div>
                <h3 className="text-xl font-bold text-[#3F3F3F] mb-3">Pour nos candidats</h3>
                <p className="text-base text-gray-600 leading-relaxed">
                  Nous valorisons votre expérience et vos compétences pour vous proposer des missions
                  qui vous correspondent, avec un accompagnement personnalisé tout au long de votre parcours.
                </p>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 md:py-24 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-12">
            <motion.p variants={fadeUp} custom={0} className="text-xs font-bold uppercase tracking-[0.2em] text-[#662483] mb-3">Nos métiers</motion.p>
            <motion.h2 variants={fadeUp} custom={1} className="text-3xl sm:text-4xl font-extrabold tracking-tight text-[#3F3F3F]">
              Nos domaines d'intervention
            </motion.h2>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Package, title: "Préparation de commandes", desc: "Picking, packing et expéditions" },
              { icon: Truck, title: "Manutention", desc: "Agents logistiques et manutentionnaires" },
              { icon: Zap, title: "Caristes", desc: "Conduite d'engins et chariots élévateurs" },
              { icon: Shield, title: "Gestion de stocks", desc: "Inventaires et optimisation des flux" },
              { icon: Clock, title: "Encadrement", desc: "Responsable d'exploitation, directeur de site" },
              { icon: Users, title: "Agents logistiques", desc: "Magasiniers et opérateurs polyvalents" },
            ].map((item, i) => (
              <motion.div key={item.title} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i} variants={fadeUp} className="bg-white border border-gray-200 p-6 card-hover">
                <div className="w-10 h-10 bg-gradient-to-r from-[#662483] to-[#0069B4] flex items-center justify-center mb-4">
                  <item.icon className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-base font-bold text-[#3F3F3F] mb-2">{item.title}</h3>
                <p className="text-sm text-gray-500">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="brand-gradient py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-4">Prêt à rejoindre l'aventure ?</h2>
          <p className="text-lg text-white/80 mb-8">Inscrivez-vous dès maintenant et découvrez les offres qui vous correspondent.</p>
          <Link to="/auth">
            <Button className="bg-[#E5CD96] text-[#3F3F3F] hover:bg-[#B8A478] font-semibold px-8 py-3 rounded-none text-base" data-testid="cta-inscription">
              Créer mon compte <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>
    </main>
  );
}
